/**
 * Proxy get handler.
 * Checks whenever the property exist on the validator instance
 * or in cachedValidators. If not it will invoke $validate to
 * resolve the unknownProperty.
 * @param {DefaultValidator} validatorInstance
 * @param {string} property - Auth.users in this case is 'users'
 * @return {DefaultValidator | *}
 */
function getPropertyHandler(validatorInstance, property) {
    if (property in validatorInstance) {
        return validatorInstance[property];
    } if (property in validatorInstance.$cachedValidators) {
        return validatorInstance.$cachedValidators[property];
    }
    return validatorInstance.$validate(property);
}

/**
 * Class base for all validator
 * contains default behaviors and constructor
 */
export default class DefaultValidator {
    /**
     * Creates a Proxy based on a new DefaultValidator instance
     * @param {string} unknownProperty - property that generates the validator
     * @param {string} $property - Parent validator $property attribute.
     * @param {Array} $permissions - permissions of the user
     * @param {boolean} isAllowed - Parent authorization for its resource.
     * @return {DefaultValidator}
     */
    constructor(unknownProperty = null, {$property = '', $permissions = null, isAllowed = true} = {}) {
        this.$property = unknownProperty;
        this.$parentProperty = $property;
        this.$permissions = $permissions;
        this.$wasAllowed = isAllowed;
        this.$cachedValidators = {};
        return new Proxy(this, {get: getPropertyHandler});
    }

    /**
     * Default validation action
     * @param {string} unknownProperty
     * @return {DefaultValidator}
     */
    $validate(unknownProperty) {
        return this.$addValidator(unknownProperty, DefaultValidator);
    }

    /**
     * Add a new validator to the $cachedValidators
     * @param {string} unknownProperty
     * @param {Symbol} ClassName - class to be instantiated
     * @param {Object} properties - properties to be passed to the instance on creation
     * @return {DefaultValidator}
     */
    $addValidator(unknownProperty, ClassName, properties = {}) {
        const params = {
            ...this,
            isAllowed: this.isAllowed, // Bug
            ...properties,
        };
        this.$cachedValidators[unknownProperty] = new ClassName(unknownProperty, params);
        return this.$cachedValidators[unknownProperty];
    }

    /**
     * Clean the $cachedValidators object
     * This method will synchronously clean the validators
     * in the $cachedValidators object and destroy references.
     */
    $destroyValidators() {
        Object.keys(this.$cachedValidators).forEach((key) => {
            this.$cachedValidators[key].$destroyValidators();
            delete this.$cachedValidators[key];
        });
    }

    /**
     * Allows chains validators with an AND test
     * @param {DefaultValidator} anotherValidator
     * @return {DefaultValidator}
     */
    and(anotherValidator) {
        return this.isAllowed ? anotherValidator : this;
    }

    /**
     * Allows chains validators with an OR test
     * @param {DefaultValidator} anotherValidator
     * @return {DefaultValidator}
     */
    or(anotherValidator) {
        return this.isAllowed ? this : anotherValidator;
    }

    /**
     * By default isAllowed based on parent validator result
     * @return {boolean}
     */
    get isAllowed() {
        return this.$wasAllowed;
    }
}
